/**
 * Registration domain glue — the FESTIPOD interpretation layered on top of the
 * GENERIC `@ng-eventually/polyfill` `inbox` mechanism (T02.b) and the low-level
 * `docs` SPARQL primitives.
 *
 * The SDK stays domain-agnostic: it knows only "deposit an opaque payload for a
 * document" and "run a SPARQL update against the real injected ng". THIS module
 * supplies the Festipod domain:
 *   - the shape of the deposit payload (`RegistrationPayload`),
 *   - how a deposit becomes a host-facing `FpNotification` (`buildNotification`),
 *   - the SPARQL DELETE-WHERE that DURABLY removes a Participation server-side
 *     (`deleteParticipation`) — the documented fallback for the CRDT resurrection
 *     bug (see caveat_participation-deletion).
 *
 * Importable by `shared/` and by domain modules (meeting/notification) — it never
 * imports a module, only the SDK. See T02.a (shapes) / T02.b (inbox).
 */

import { inbox, docs } from '@ng-eventually/polyfill';
import type { Nuri, NuriLike } from '@ng-eventually/polyfill';
import { sessionPromise } from '../utils/ngSession';
import { listMyEntityDocs } from '../utils/storeRegistry';
import { canonicalDocumentId } from '../utils/documentNuri';
import { escapeLiteral, escapeIri } from './sparqlEscape';
import type { FpNotificationData } from './types';

/** Notification IRI/type constants (mirror the SHEX Notification shape). */
export const NOTIF_TYPE_NEW_PARTICIPANT = 'new-participant';
/** Leave marker deposited by a registrant on `leaveEvent` (Option B, symmetric).
 *  The owner materializes it to remove the registration from the active set. */
export const NOTIF_TYPE_LEAVE_PARTICIPANT = 'leave-participant';
const NOTIF_TYPE_IRI = 'http://festipod.org/Notification';
const P = {
  recipient: 'http://festipod.org/recipient',
  type: 'http://festipod.org/type',
  ref: 'http://festipod.org/ref',
  payload: 'http://festipod.org/payload',
  timestamp: 'http://festipod.org/timestamp',
  isRead: 'http://festipod.org/isRead',
  partType: 'http://festipod.org/Participation',
  partEvent: 'http://festipod.org/event',
  partUser: 'http://festipod.org/user',
} as const;

/**
 * The opaque payload a registrant deposits into the host's inbox on join. The
 * lib treats this as `unknown`; only this domain module reads its fields.
 */
export interface RegistrationPayload {
  /** `new-participant` on join, `leave-participant` on leave (Option B: the owner
   *  materializes both markers to derive the active-registration SET). */
  kind: typeof NOTIF_TYPE_NEW_PARTICIPANT | typeof NOTIF_TYPE_LEAVE_PARTICIPANT;
  eventId: string;
  /** The registrant's user id, or null when the deposit was anonymous. */
  userId: string | null;
  /**
   * A STABLE, per-deposit unique id minted at deposit time (F5 dedup). The lib's
   * `Deposit` surfaces only `{ from, payload, ts }` — no stable id — so two
   * deposits in the same ms by the same anon principal would otherwise both mint
   * `notif-${ts}-anon` and collide (a re-join would silently duplicate OR be
   * dropped by the seen-set). Carrying our own `uid` in the payload makes the
   * derived notification id collision-free without changing the lib.
   *
   * OPTION B — this uid is ALSO the pivot of the owner's idempotent
   * materialization: a `new-participant` deposit's uid keys the registration in
   * the active SET, and a matching `leave-participant` carries the SAME `regUid`
   * (see below) to remove it. Deriving the count from the SET makes a broker
   * re-sync (which replays the same deposits) converge — never double-count. */
  uid: string;
  /**
   * The registrant's OWN participation-document NURI (protected, owned by the
   * registrant). Carried on `new-participant` so the owner — if a connection of
   * the registrant — could read the participation in clear. The count itself does
   * NOT depend on reading it (the owner only counts distinct active registrations
   * from the inbox markers), so a non-connection owner still counts correctly. */
  participationDoc?: string;
  /**
   * On a `leave-participant` deposit: the `uid` of the `new-participant` deposit
   * this leave cancels, so the owner removes exactly that registration from the
   * active set. When the join uid is not known (e.g. a leave with no prior join
   * in this session), the owner falls back to cancelling by (eventId, userId).  */
  regUid?: string;
}

/** Mint a stable, collision-resistant per-deposit uid (time + randomness). */
function mintDepositUid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * CANONICAL event-id — the id-form the owner-materializer matches deposits ON.
 * DEFENSIVE invariant, not a fix for an active bug.
 *
 * An event's `@id` is its document NURI, a `did:ng:o:<repo>[:v:<overlay>]`. On the
 * current tree the SAME event carries the IDENTICAL NURI (incl. any `:v:<overlay>`)
 * across every boundary — create-time / `listMyEntityDocs` and the read `@id` all
 * agree, because `readUnion` pins the subject to the input doc NURI (lib
 * `read-model.ts`). So matching already works. This canonicalization GUARDS that:
 * `ownedEventIds` (what the materializer iterates), the joiner's DEPOSIT key
 * (`payload.eventId`) and the count-write target are all matched on ONE canonical
 * form — the BASE repo id (strip any `:v:<overlay>` suffix) — so that should an
 * overlay-form ever diverge across those paths, the owner-materializer still
 * matches instead of silently returning 0 (a no-op count that never converges).
 * Only the MATCHING uses the stripped form — the count is still WRITTEN on the real
 * (owned) NURI, a live openable doc NURI (a stripped id must never be a write/anchor
 * target).
 *
 * A NURI with no `:v:` overlay (or a non-`did:ng:o:` id) passes through unchanged.
 *
 * The canonicalization itself is `canonicalDocumentId` (utils/documentNuri): the
 * SAME invariant also keys the app's one-inbox-per-document resolution, and one
 * document must not have two canonical forms depending on who is asking.
 */
export const canonicalEventId = canonicalDocumentId;

/**
 * Build the host-facing notification from a registration deposit. The recipient
 * is the event host; `ref` points at the event; the payload carries the raw
 * registration deposit (who joined). Kept pure so callers own persistence.
 */
export function buildNotification(
  recipientId: string,
  eventId: string,
  registrantId: string | null,
  ts: number,
): Omit<FpNotificationData, 'id'> {
  return {
    recipientId,
    type: NOTIF_TYPE_NEW_PARTICIPANT,
    ref: eventId,
    payload: JSON.stringify({ eventId, userId: registrantId }),
    timestamp: new Date(ts).toISOString(),
    isRead: false,
  };
}

/**
 * Deposit a registration FOR THE EVENT DOCUMENT (`inbox.postToDocument`) +
 * return the deposit ts so the caller can mint a matching notification.
 *
 * A deposit names the DOCUMENT it is about, never an address: the depositor
 * holds no inbox address and needs none. Only the event's owner reads it back.
 *
 * The registrant identity travels in the PAYLOAD (`userId`), which the host
 * materializer reads. The transport-level `from` is left ANONYMOUS (`null`): the
 * SDK binds `from` to the depositor's own identity and rejects a mismatched one
 * as a spoof, and the app's user IRI is not the declared identity key — so the
 * domain identity belongs in the payload, not in the transport `from`.
 */
export async function depositRegistration(
  eventDoc: NuriLike,
  registrantId: string | null,
  participationDoc?: string,
): Promise<{ ts: number; uid: string }> {
  const ts = Date.now();
  const uid = mintDepositUid();
  // One document per entity: the event's `@id` IS its document, so the deposit
  // target and the payload's event key are the same value.
  const eventId: string = eventDoc;
  const payload: RegistrationPayload = {
    kind: NOTIF_TYPE_NEW_PARTICIPANT,
    eventId,
    userId: registrantId,
    uid,
    participationDoc,
  };
  await inbox.postToDocument(eventDoc, { from: null, payload, ts });
  console.log(
    `[Attendance] deposit new-participant → inbox for event=${canonicalEventId(eventId)} ` +
      `user=${registrantId ?? '(anon)'} uid=${uid}`,
  );
  return { ts, uid };
}

/**
 * Deposit a LEAVE marker FOR THE EVENT DOCUMENT (Option B, symmetric to
 * `depositRegistration`). The owner materializes it to remove the matching
 * registration from the active set. `regUid` (the join deposit's uid) lets the
 * owner cancel exactly that registration; when unknown, the owner falls back to
 * (eventId, userId). Idempotent by the leave's own `uid` and by `regUid`: a
 * re-synced leave removes an already-removed registration → no double-decrement.
 */
export async function depositLeave(
  eventDoc: NuriLike,
  registrantId: string | null,
  regUid?: string,
): Promise<{ ts: number; uid: string }> {
  const ts = Date.now();
  const uid = mintDepositUid();
  const eventId: string = eventDoc;
  const payload: RegistrationPayload = {
    kind: NOTIF_TYPE_LEAVE_PARTICIPANT,
    eventId,
    userId: registrantId,
    uid,
    regUid,
  };
  await inbox.postToDocument(eventDoc, { from: null, payload, ts });
  console.log(
    `[Attendance] deposit leave-participant → inbox for event=${canonicalEventId(eventId)} ` +
      `user=${registrantId ?? '(anon)'} uid=${uid} regUid=${regUid ?? '(none)'}`,
  );
  return { ts, uid };
}

/** One active registration the owner has materialized from the inbox. */
export interface ActiveRegistration {
  /** The join deposit's stable uid — the identity of this registration. */
  uid: string;
  /** The registrant's user id (or null when the deposit was anonymous). */
  userId: string | null;
  /** The registrant's participation-doc NURI when carried on the join. */
  participationDoc?: string;
  /** The join deposit timestamp. */
  ts: number;
}

/**
 * OPTION B — OWNER MATERIALIZATION (pure, idempotent, replay-safe).
 *
 * Reads the event's inbox and derives the SET of DISTINCT ACTIVE registrations
 * for `eventId`: every `new-participant` deposit, keyed by its stable `uid`,
 * MINUS every registration a later `leave-participant` cancels. A leave cancels
 * by `regUid` (the exact join uid) when carried, else by matching `userId`
 * (best-effort for a leave whose join uid this session never saw).
 *
 * The count is a PURE FUNCTION of the current inbox contents, so it CONVERGES:
 *  - re-reading the same inbox (a broker re-sync replays the same deposits)
 *    yields the SAME set → never double-counts a join nor double-decrements a
 *    leave (the crux of idempotence);
 *  - dedup is by the join `uid`, so a duplicated deposit collapses to one entry;
 *  - a leave for an unknown/already-removed registration is simply a no-op on the
 *    set — it can never resurrect a phantom count.
 *
 * The owner then writes `participantCount` on its OWN event doc as
 * `activeRegistrations.size`. There is NO host baseline: the creator merely
 * signals a public event and is NOT obliged to participate (no host notion), so
 * the count is 0 until someone joins, and the creator may join/leave like anyone.
 */
export async function materializeAttendance(
  targetInbox: Nuri,
  eventId: string,
): Promise<ActiveRegistration[]> {
  // `inbox.readSynced`, not `inbox.read` — the two differ by CONTRACT, and this
  // call site needs the stronger one. `read` returns what is known locally right
  // now; `readSynced` returns once the deposits synced to this inbox are visible.
  // The owner materializes at its NEXT CONNECTION, i.e. from a cold session where
  // "known locally right now" is not yet the truth — so the synced view is the
  // only correct one here. Single wait, single read: no polling
  // (see `rule_no-broker-polling`).
  const deposits = await inbox.readSynced(targetInbox);
  // Match deposits to this event on the CANONICAL id-form (base repo id, stripping
  // any `:v:<overlay>` suffix). On the current tree the forms already agree, but
  // matching on the canonical base id GUARDS against a future overlay-form drift
  // between `payload.eventId` and this owned `eventId` (see `canonicalEventId`).
  const canonId = canonicalEventId(eventId);
  console.log(
    `[Attendance] materializeAttendance — inbox synced+read for event=${canonId}: ${deposits.length} deposit(s) total in inbox`,
  );
  // First pass: collect distinct joins by uid; collect leave cancellations.
  const joins = new Map<string, ActiveRegistration>();
  const cancelledUids = new Set<string>();
  const leaveUserIds: Array<string | null> = [];
  for (const d of deposits) {
    const p = d.payload as Partial<RegistrationPayload> | null;
    if (!p || !p.eventId || canonicalEventId(p.eventId) !== canonId || !p.uid) continue;
    if (p.kind === NOTIF_TYPE_NEW_PARTICIPANT) {
      if (!joins.has(p.uid)) {
        joins.set(p.uid, {
          uid: p.uid,
          userId: p.userId ?? null,
          participationDoc: p.participationDoc,
          ts: d.ts,
        });
      }
    } else if (p.kind === NOTIF_TYPE_LEAVE_PARTICIPANT) {
      if (p.regUid) cancelledUids.add(p.regUid);
      else leaveUserIds.push(p.userId ?? null);
    }
  }
  // Apply cancellations: by exact join uid first, then by userId fallback (cancel
  // the earliest still-active join for that user, so N leaves cancel N joins).
  for (const uid of cancelledUids) joins.delete(uid);
  for (const leaverId of leaveUserIds) {
    if (leaverId == null) continue; // anonymous leave can't be matched by user
    const victim = [...joins.values()]
      .filter(r => r.userId === leaverId)
      .sort((a, b) => a.ts - b.ts)[0];
    if (victim) joins.delete(victim.uid);
  }
  const active = [...joins.values()].sort((a, b) => a.ts - b.ts);
  console.log(
    `[Attendance] materializeAttendance — event=${canonId}: ${active.length} active ` +
      `registration(s) [leaves: byUid=${cancelledUids.size}, byUser=${leaveUserIds.length}] → ` +
      `uids=[${active.map(r => r.uid).join(', ')}]`,
  );
  return active;
}

/**
 * Materialize a host inbox's deposits into host-facing notifications (data-level
 * surfacing). The inbox read (`inbox.read`) returns the raw deposits; we
 * map each registration deposit to an `FpNotificationData` for `recipientId`.
 */
export async function readRegistrationNotifications(
  targetInbox: Nuri,
  recipientEventId: string,
): Promise<FpNotificationData[]> {
  const deposits = await inbox.read(targetInbox);
  const canonRecipient = recipientEventId ? canonicalEventId(recipientEventId) : '';
  const notifs: FpNotificationData[] = [];
  for (const d of deposits) {
    const p = d.payload as Partial<RegistrationPayload> | null;
    if (!p || p.kind !== NOTIF_TYPE_NEW_PARTICIPANT || !p.eventId) continue;
    // Keep only deposits for the event whose host is reading, matched on the
    // CANONICAL id-form (base repo id) so an overlay difference never drops a
    // deposit. `recipientEventId` doubles as the recipient.
    if (canonRecipient && canonicalEventId(p.eventId) !== canonRecipient) continue;
    const built = buildNotification(recipientEventId, p.eventId, d.from ?? null, d.ts);
    // F5 dedup: prefer the stable per-deposit uid carried in the payload so
    // same-ms / anonymous deposits never collide. Fall back to the legacy
    // ts+principal id for deposits minted before the uid existed.
    const id = p.uid ? `notif-${p.uid}` : `notif-${d.ts}-${p.userId ?? 'anon'}`;
    notifs.push({ ...built, id });
  }
  return notifs;
}

/**
 * AUTHORITATIVE count of a user's Participations to an event across the user's OWN
 * protected per-entity documents (the broker, not the reactive set). Used to make
 * join IDEMPOTENT reliably: the reactive participation set can lag behind a
 * just-written participation, so a second join checking only the reactive set would
 * write a duplicate. Querying the broker sees the real state regardless of read lag.
 *
 * Scoped to MY OWN protected documents via `listMyEntityDocs` — a user's own
 * participations live in their own documents, so there is NO need to fan out over
 * anyone else's (which would open/sync unsynced docs → the ~75s hang).
 */
export async function countUserParticipations(
  eventId: string,
  userId: string,
): Promise<number> {
  const sid = (await sessionPromise).session_id;
  const docs_ = await listMyEntityDocs('protected');
  let total = 0;
  for (const g of docs_) {
    // NO `.catch(() => 0)` here. A failed count means UNKNOWN, never zero: this
    // number decides whether a participation already exists, and answering "none"
    // when we could not find out is what writes a duplicate. The rejection travels
    // to the caller, which surfaces it.
    total += await countParticipations(sid, g, eventId, userId);
  }
  return total;
}

/**
 * How the deletion identified the Participation, for the caller's verification.
 * `remaining` is the authoritative post-delete count of Participations still
 * matching (event, user) in `graphNuri` — re-queried from the broker AFTER the
 * update. A durable delete leaves `remaining === 0`; a non-zero value means the
 * delete matched nothing (or partially), so the caller must NOT flip the UI.
 */
export interface DeleteParticipationResult {
  /** Participations matching (event, user) BEFORE the delete (re-queried). */
  before: number;
  /** Participations matching (event, user) AFTER the delete (re-queried). */
  remaining: number;
  /** Whether we deleted by the participation's own subject IRI (vs. fallback). */
  bySubject: boolean;
}

/** Count Participations matching (event, user) in `graphNuri`, authoritatively
 *  (re-query the real broker via `docs.sparqlQuery`). We match ?event / ?user
 *  by IRI OR by literal string value so the count is tolerant of how the ORM
 *  serialized these fields — this is a COUNT (read-only), so tolerance here is
 *  safe (unlike a DELETE, it can never over-remove). */
async function countParticipations(
  sid: string | number,
  graphNuri: NuriLike,
  eventId: string,
  userId: string,
): Promise<number> {
  const evL = escapeLiteral(eventId);
  const usL = escapeLiteral(userId);
  // NO explicit `GRAPH <…>` wrapper: participations are written by `writeEntity`
  // into the anchored DEFAULT graph (one doc per entity), so this count reads that
  // same anchored default graph — anchored to `graphNuri`, no `GRAPH` clause. This
  // is the CANONICAL SDK read/write shape (write and read the same anchored default
  // graph); SDK graph details live in `@ng-eventually/polyfill`, not here.
  const query = `
    SELECT (COUNT(DISTINCT ?s) AS ?n) WHERE {
      ?s a <${P.partType}> ;
         <${P.partEvent}> ?event ;
         <${P.partUser}> ?user .
      FILTER( STR(?event) = "${evL}" && STR(?user) = "${usL}" )
    }`;
  const result = await docs.sparqlQuery(sid, query, undefined, graphNuri);
  // Tolerant binding extraction (mirrors the lib's readBindings shape).
  const anyRes = result as {
    results?: { bindings?: Array<Record<string, { value?: string }>> };
  };
  const rows = Array.isArray(result)
    ? (result as Array<Record<string, { value?: string }>>)
    : anyRes.results?.bindings ?? [];
  const raw = rows[0]?.n?.value ?? '0';
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * DURABLY delete a Participation server-side via SPARQL DELETE-WHERE (the real
 * injected `ng` through `docs.sparqlUpdate`) — the documented fallback for the
 * CRDT resurrection bug (see caveat_participation-deletion): `ngSet.delete()`
 * triggers reactivity but the item resurrects via broker sync. This removes
 * every triple of the matching Participation subject in `graphNuri`, then
 * re-queries the broker to CONFIRM the removal (returns the affected counts so
 * the caller can verify before flipping the UI).
 *
 * Identification (durable, removes ALL matches — the broker can hold DUPLICATE
 * participations for one (event, user)):
 *  1. AUTHORITATIVE SWEEP by (event, user) — binds ?event/?user by IRI value
 *     (`sameTerm`) AND literal string value, covering the absolute IRI form and
 *     any base-resolved literal form (NOT the weak `STR()`-only match of the F2
 *     bug). Removes every matching participation, including duplicates.
 *  2. BELT-AND-SUSPENDERS by the participation's OWN subject IRI (`subjectIri`,
 *     from `ngPart["@id"]`) when known — exact, bound as an IRI, catches any
 *     residual the sweep missed on object-form drift.
 * Only deleting the single reactive subject (the naive fix) leaves duplicates
 * behind → the participation resurrects.
 *
 * IMPORTANT (caveat): do NOT rely on `ngSet.delete()` to persist the removal in
 * the SAME flow — combining the two paths creates a CRDT conflict. The caller
 * uses THIS as the authoritative delete and only reflects the result in reactive
 * state for the immediate UI, AND only once `remaining === 0`.
 */
export async function deleteParticipation(
  graphNuri: NuriLike,
  eventId: string,
  userId: string,
  subjectIri?: string,
): Promise<DeleteParticipationResult> {
  const sid = (await sessionPromise).session_id;
  // The ORM batches `ngSet.add` into a microtask + the broker needs a moment to
  // land it in the SPARQL-queryable graph. A leave that follows a join tightly
  // (tests; a fast user) can reach here BEFORE the join's write is queryable —
  // deleting then would no-op on absent triples (the F2 resurrection root). Flush
  // the ORM microtask and poll the authoritative count until the participation is
  // visible (bounded) so the delete operates on real triples. In the running app
  // the triple is already present, so the first poll returns immediately.
  await Promise.resolve(); // flush pending ORM microtask batch
  let before = await countParticipations(sid, graphNuri, eventId, userId);
  for (let i = 0; before === 0 && i < 10; i++) {
    await new Promise(r => setTimeout(r, 100));
    before = await countParticipations(sid, graphNuri, eventId, userId);
  }

  // A usable subject IRI is a non-empty, IRI-safe value (the ORM assigns "" to
  // freshly-added, not-yet-persisted items — that's not a real subject).
  const hasSubject = typeof subjectIri === 'string' && subjectIri.length > 0;

  // AUTHORITATIVE SWEEP: delete EVERY Participation matching (event, user) — not
  // just the one subject the reactive set surfaced. The broker can hold DUPLICATE
  // participations for the same (event, user) (observed: a re-join / stale item
  // leaves 2); deleting only `ngPart["@id"]` removes one and the other survives →
  // the désinscription silently no-ops on the duplicate and resurrects. So the
  // durable delete matches by (event, user), binding ?event/?user by IRI value
  // (sameTerm) AND by literal string value — covering the absolute IRI form and
  // any base-resolved literal form (NOT the weak `STR()`-only match of the F2 bug).
  const evIri = escapeIri(eventId);
  const usIri = escapeIri(userId);
  const evL = escapeLiteral(eventId);
  const usL = escapeLiteral(userId);
  // NO explicit `GRAPH <…>` wrapper: participations live in the anchored DEFAULT
  // graph (writeEntity), so the sweep DELETEs from that same anchored default
  // graph — anchored to `graphNuri`, no `GRAPH` clause. Write, read and delete all
  // use the one CANONICAL anchored-default-graph shape so they stay consistent (a
  // mismatched target here would no-op the delete → the F2 resurrection). SDK graph
  // details live in `@ng-eventually/polyfill`, not here.
  const sweep = `
    DELETE { ?s ?p ?o }
    WHERE {
      ?s a <${P.partType}> ;
         <${P.partEvent}> ?event ;
         <${P.partUser}> ?user ;
         ?p ?o .
      FILTER(
        ( sameTerm(?event, <${evIri}>) || STR(?event) = "${evL}" ) &&
        ( sameTerm(?user, <${usIri}>) || STR(?user) = "${usL}" )
      )
    }`;
  await docs.sparqlUpdate(sid, sweep, graphNuri);

  // BELT-AND-SUSPENDERS: also delete by the participation's OWN subject IRI when
  // known. This catches the residual case where an object-form drift makes the
  // (event, user) sweep miss a subject we nonetheless hold the id for — exact,
  // bound as an IRI, cannot no-op on drift.
  if (subjectIri && hasSubject) {
    const s = escapeIri(subjectIri);
    // Anchored default-graph (no `GRAPH` clause), like the sweep above.
    const bySubject = `
      DELETE { <${s}> ?p ?o }
      WHERE { <${s}> ?p ?o }`;
    await docs.sparqlUpdate(sid, bySubject, graphNuri);
  }

  // Re-query the broker to CONFIRM every matching participation is gone
  // (authoritative — not the reactive set). Caller checks `remaining === 0`.
  const remaining = await countParticipations(sid, graphNuri, eventId, userId);
  return { before, remaining, bySubject: hasSubject };
}

/**
 * Persist a host notification server-side as an `FpNotification` (SHEX shape,
 * T02.a) via SPARQL INSERT DATA into `graphNuri`. Complements the reactive path;
 * used so notifications survive a refresh at the data level.
 */
export async function insertNotification(
  graphNuri: Nuri,
  notif: Omit<FpNotificationData, 'id'>,
): Promise<string> {
  const sid = (await sessionPromise).session_id;
  const subject = `urn:festipod:notif:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  // recipient/ref are bare domain ids ("user-1", "event-1"), not absolute IRIs;
  // store them as string literals to keep the INSERT valid (the raw shape read
  // is not the primary surfacing path — the inbox read is). Every literal is
  // escaped via the app's escapeLiteral (guards \ " \n \r \t — SPARQL injection).
  const refTriple = notif.ref ? `\n      <${P.ref}> "${escapeLiteral(notif.ref)}" ;` : '';
  const payloadTriple = notif.payload
    ? `\n      <${P.payload}> "${escapeLiteral(notif.payload)}" ;`
    : '';
  // NO explicit `GRAPH <…>` wrapper: anchored to `graphNuri`, the INSERT lands in
  // that doc's anchored DEFAULT graph — the CANONICAL SDK write shape, consistent
  // with every other per-entity write (writeEntity / updateEntityField). SDK graph
  // details live in `@ng-eventually/polyfill`, not here.
  const update = `
    INSERT DATA {
      <${escapeIri(subject)}> a <${NOTIF_TYPE_IRI}> ;
      <${P.recipient}> "${escapeLiteral(notif.recipientId)}" ;
      <${P.type}> "${escapeLiteral(notif.type)}" ;${refTriple}${payloadTriple}
      <${P.timestamp}> "${escapeLiteral(notif.timestamp)}" ;
      <${P.isRead}> "${notif.isRead}" .
    }`;
  await docs.sparqlUpdate(sid, update, graphNuri);
  return subject;
}
